import * as React from 'react';
import Typography from '@mui/material/Typography';
import { Container, Button, Box, Grid, InputLabel, Select, MenuItem, FormControl, FormHelperText } from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { z } from "zod";
import { useFormContext, Controller } from "react-hook-form";
import { FormModal, RHFTextField, RHFSelect } from "../components/form";
import api, { fetchDeviceHistory, fetchCustomers, fetchVehicles, fetchSolutions } from "../services/ApiService";
import DataTable from '../components/DataTable';

const attachDeviceSchema = z.object({
    solution: z.string().min(1, "Required"),
    customer: z.string().min(1, "Required"),
    vehicle: z.string().min(1, "Required"),
    date: z.string().min(1, "Required"),
    type: z.string().min(1, "Required"),
    cost: z.string().optional(),
    discount: z.string().optional(),
    discountedCost: z.string().optional(),
    duration: z.string().optional(),
    remarks: z.string().optional(),
});

const CostCalculator = () => {
    const { watch, setValue } = useFormContext();
    const [cost, discount] = watch(["cost", "discount"]);
    React.useEffect(() => {
        const c = parseFloat(cost || 0);
        const d = parseFloat(discount || 0);
        setValue("discountedCost", String(Math.round(c - c * d / 100)));
    }, [cost, discount, setValue]);
    return null;
};

export function DeviceHistory() {
    const [devicesOptions, setDevicesOptions] = React.useState({});
    const [devices, setDevices] = React.useState({});
    const [haulierOptions, setHaulierOptions] = React.useState({});
    const [employees, setEmployees] = React.useState([]);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState();

    React.useEffect(() => {
        fetchHauliers();
        fetchDevices();
        fetchDeliveries();
    }, []);

    const fetchDeliveries = async () => {
        try {
            const response = await fetchDeviceHistory();
            console.log(response.data, 'employees');
            setEmployees(response.data);
        } catch (error) {
            console.error(error.response?.statusText || error.message || 'Error fetching device history');
        }
    };

    const fetchHauliers = async () => {
        try {
            const response = await fetchCustomers();
            const haulierDict = response.data.reduce((acc, item) => {
                acc[item.id] = item.companyName;
                return acc;
            }, {});
            setHaulierOptions(haulierDict);
            console.log(haulierDict);
        } catch (error) {
            console.error(error.response?.statusText || error.message || 'Error fetching customers');
        }
    };

    const fetchDevices = async () => {
        try {
            const response = await fetchSolutions();
            const deviceDict = response.data.reduce((acc, item) => {
                acc[item.id] = item.name;
                return acc;
            }, {});
            const deviceCostMap = response.data.reduce((acc, item) => {
                acc[item.id] = item.cost;
                return acc;
            }, {});
            console.log(deviceDict);
            setDevicesOptions(deviceDict);
            setDevices(deviceCostMap);
        } catch (error) {
            console.error(error.response?.statusText || error.message || 'Error fetching solutions');
        }
    };

    const handleOpen = (row) => {
        setModalOpen(true);
        setSelectedRow(row);
    };
    const handleClose = () => setModalOpen(false);

    const handleExport = () => {
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(18);
        doc.text('Employees Report', 14, 16);
        const tableData = employees.map(record => [
            devicesOptions[record.device] || '',
            record.delivery,
            record.installation_date,
            record.removal_date,
        ]);
        const columns = ['Device', 'Delivery', 'Installation Date', 'Removal Date'];
        doc.autoTable({
            head: [columns],
            body: tableData,
            startY: 30,
            styles: { fontSize: 10, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 60 },
                1: { cellWidth: 40 },
                2: { cellWidth: 45 },
                3: { cellWidth: 45 },
            },
            theme: 'grid',
            didDrawPage: () => {
                doc.setFontSize(18);
                doc.text('Employees Report', 14, 16);
            },
        });
        doc.save('Device Report.pdf');
    };

    const EmployeeModal = ({ selectedRow, open, handleClose }) => {
        console.log(selectedRow, 'selectedRow');
        const [trucks, setTrucks] = React.useState([]);
        const [documentFile, setDocumentFile] = React.useState(null);

        const fetchTruck = async (customerId) => {
            console.log(customerId);
            try {
                const response = await fetchVehicles(customerId);
                console.log(response.data);
                const allNames = response.data.map((vehicle) => vehicle.number);
                setTrucks(allNames);
            } catch (error) {
                console.error('Error fetching trucks:', error);
            }
        };

        return (
            <FormModal
                open={open}
                onClose={handleClose}
                title="Attach Device"
                schema={attachDeviceSchema}
                defaultValues={{
                    solution: "",
                    customer: "",
                    vehicle: "",
                    cost: "",
                    discount: "",
                    discountedCost: "",
                    date: "",
                    type: "",
                    duration: "",
                    remarks: "",
                }}
                onSubmit={async (data) => {
                    const fd = new FormData();
                    Object.entries(data).forEach(([k, v]) => {
                        if (v !== null && v !== undefined && v !== "") fd.append(k, v);
                    });
                    if (documentFile) fd.append("document", documentFile);
                    try {
                        const response = await api.post('/sales-coordinator-new-installation/', fd, { responseType: 'blob' });
                        console.log(response);
                        if (response.status === 200) {
                            const blob = response.data;
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', 'truck_info.html');
                            document.body.appendChild(link);
                            link.target = '_blank';
                            link.click();
                            link.parentNode.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        }
                        fetchDeliveries();
                    } catch (err) {
                        console.error("Error creating installation:", err);
                        throw err;
                    }
                }}
                submitLabel="Add"
                width={400}
            >
                {({ control, watch, setValue }) => {
                    const customer = watch("customer");
                    const type = watch("type");
                    return (
                        <>
                            <CostCalculator />
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        control={control}
                                        name="solution"
                                        defaultValue=""
                                        render={({ field, fieldState }) => (
                                            <FormControl fullWidth required error={!!fieldState.error}>
                                                <InputLabel>Solution</InputLabel>
                                                <Select
                                                    {...field}
                                                    label="Solution"
                                                    value={field.value ?? ""}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value);
                                                        setValue("cost", String(devices[e.target.value] || ""));
                                                    }}
                                                >
                                                    {Object.entries(devicesOptions).map(([key, value]) => (
                                                        <MenuItem key={key} value={String(key)}>{value}</MenuItem>
                                                    ))}
                                                </Select>
                                                {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Controller
                                        control={control}
                                        name="customer"
                                        defaultValue=""
                                        render={({ field, fieldState }) => (
                                            <FormControl fullWidth required error={!!fieldState.error}>
                                                <InputLabel>Customer</InputLabel>
                                                <Select
                                                    {...field}
                                                    label="Customer"
                                                    value={field.value ?? ""}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value);
                                                        setTrucks([]);
                                                        fetchTruck(e.target.value);
                                                    }}
                                                >
                                                    {Object.entries(haulierOptions).map(([key, value]) => (
                                                        <MenuItem key={key} value={String(key)}>{value}</MenuItem>
                                                    ))}
                                                </Select>
                                                {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                {customer && trucks.length > 0 && (
                                    <Grid item xs={12} md={6}>
                                        <RHFSelect control={control} name="vehicle" label="Vehicle" required>
                                            {trucks.map((truck, index) => (
                                                <MenuItem key={index} value={truck}>{truck}</MenuItem>
                                            ))}
                                        </RHFSelect>
                                    </Grid>
                                )}

                                <Grid item xs={12} md={6}>
                                    <RHFTextField control={control} name="cost" label="Cost" disabled />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <RHFTextField control={control} name="discount" label="Discount in %" />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <RHFTextField control={control} name="discountedCost" label="Discounted Cost" disabled />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <RHFTextField control={control} name="remarks" label="Remarks" />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <RHFTextField
                                        control={control}
                                        name="date"
                                        label="Date"
                                        type="datetime-local"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <RHFSelect control={control} name="type" label="Type" required>
                                        <MenuItem value="Annual">Annual</MenuItem>
                                        <MenuItem value="Rental">Rental</MenuItem>
                                    </RHFSelect>
                                </Grid>

                                {type === "Rental" && (
                                    <Grid item xs={12} md={6}>
                                        <RHFSelect control={control} name="duration" label="Duration">
                                            <MenuItem value="3 Month">3 Month</MenuItem>
                                            <MenuItem value="6 Month">6 Month</MenuItem>
                                            <MenuItem value="9 Month">9 Month</MenuItem>
                                            <MenuItem value="12 Month">12 Month</MenuItem>
                                        </RHFSelect>
                                    </Grid>
                                )}
                            </Grid>

                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                                    Document (optional)
                                </Typography>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt,image/*"
                                    style={{ width: "100%" }}
                                    onChange={(e) => setDocumentFile(e.target.files?.[0] ?? null)}
                                />
                            </Box>
                        </>
                    );
                }}
            </FormModal>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ m: 2 }}>
            <EmployeeModal selectedRow={selectedRow} open={modalOpen} handleClose={handleClose} />
            <DataTable
                title="Solutions Attach History"
                columns={[
                    { key: 'delivery', label: 'Attachment #' },
                    { key: 'installation_date', label: 'Installation Date' },
                    { key: 'removal_date', label: 'Removal Date' },
                ]}
                rows={employees}
                toolbarActions={<Button variant="contained" color="primary" onClick={handleExport}>Export to PDF</Button>}
            />
        </Container>
    );
}
